import type { TemplateResult } from 'lit'
import { nothing } from 'lit'

import type {
  CellContent,
  HtmlCellContent,
  RenderCallbackContent,
  StyledCellContent,
} from '../types/cell-content'

/**
 * Type guard to check if content is a Lit TemplateResult
 */
function isTemplateResult(content: unknown): content is TemplateResult {
  return (
    typeof content === 'object' &&
    content !== null &&
    '_$litType$' in content &&
    'strings' in content &&
    'values' in content
  )
}

/**
 * Type guard to check if content is a styled cell content object
 */
function isStyledCellContent(content: unknown): content is StyledCellContent {
  return (
    typeof content === 'object' &&
    content !== null &&
    'value' in content &&
    ('style' in content || 'className' in content)
  )
}

/**
 * Type guard to check if content is an HTML cell content object
 */
function isHtmlCellContent(content: unknown): content is HtmlCellContent {
  return typeof content === 'object' && content !== null && 'html' in content
}

/**
 * Type guard to check if content is a render callback object
 */
function isRenderCallbackContent(
  content: unknown,
): content is RenderCallbackContent {
  return (
    typeof content === 'object' &&
    content !== null &&
    'render' in content &&
    typeof (content as RenderCallbackContent).render === 'function'
  )
}

/**
 * Sanitize HTML string using DOMPurify
 * Falls back to basic escaping if DOMPurify is not available
 */
function sanitizeHtml(html: string): string {
  // TODO: Add DOMPurify dependency and use it for sanitization
  // For now, use basic escaping
  const div = document.createElement('div')
  div.textContent = html
  return div.innerHTML
}

/**
 * Apply styles to a styled cell content
 */
function applyStyledValue(content: StyledCellContent) {
  const { value, style, className } = content

  // Create wrapper span with styles
  const span = document.createElement('span')

  // Apply className if provided
  if (className) {
    span.className = className
  }

  // Apply inline styles if provided
  if (style) {
    Object.entries(style).forEach(([key, val]) => {
      if (val !== undefined && val !== null) {
        // Convert camelCase to kebab-case for CSS properties
        const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase()
        span.style.setProperty(cssKey, String(val))
      }
    })
  }

  // Set content
  span.textContent = value != null ? String(value) : ''

  return span
}

/**
 * Render HTML string content
 */
function renderHtmlContent(content: HtmlCellContent) {
  const { html: htmlString, sanitize = true } = content

  const container = document.createElement('div')

  if (sanitize) {
    // Sanitize HTML before rendering
    container.innerHTML = sanitizeHtml(htmlString)
  } else {
    // Use unsanitized HTML (dangerous!)
    console.warn(
      'Rendering unsanitized HTML in datatable cell. This may be a security risk.',
    )
    container.innerHTML = htmlString
  }

  // Return the first child if single element, otherwise return container
  return container.children.length === 1
    ? (container.firstElementChild as HTMLElement)
    : container
}

/**
 * Render callback content
 * Returns a Lit directive that calls the render callback
 */
function renderCallbackContent(content: RenderCallbackContent) {
  const { render: renderFn, cleanup } = content

  // Create container element
  const container = document.createElement('div')
  container.className = 'cell-render-container'

  // Call render function
  try {
    renderFn(container)
  } catch (error) {
    console.error('Error rendering cell content:', error)
    container.textContent = 'Error rendering cell'
  }

  // Store cleanup function for later
  if (cleanup) {
    // Attach cleanup to container for lifecycle management
    ;(container as any).__cleanup = cleanup
  }

  return container
}

/**
 * Main cell content renderer
 * Detects content type and returns appropriate rendering
 *
 * @param content - The cell content in any supported format
 * @returns Rendered content (TemplateResult, HTMLElement, or primitive)
 */
export function renderCellContent(content: CellContent): unknown {
  // Handle null/undefined
  if (content === null || content === undefined) {
    return nothing
  }

  // Format 6: Lit TemplateResult - pass through unchanged
  if (isTemplateResult(content)) {
    return content
  }

  // Format 3: HTMLElement - pass through unchanged
  if (content instanceof HTMLElement) {
    return content
  }

  // Format 5: Render Callback - execute and return element
  if (isRenderCallbackContent(content)) {
    return renderCallbackContent(content)
  }

  // Format 4: HTML String - sanitize and render
  if (isHtmlCellContent(content)) {
    return renderHtmlContent(content)
  }

  // Format 2: Styled Object - apply styles and return
  if (isStyledCellContent(content)) {
    return applyStyledValue(content)
  }

  // Format 1: Primitive Value - render directly
  // (string, number, boolean)
  return content
}

/**
 * Cleanup cell content
 * Calls cleanup callbacks if present
 *
 * @param element - The rendered element that may contain cleanup callbacks
 */
export function cleanupCellContent(element: unknown): void {
  if (element instanceof HTMLElement) {
    const cleanup = (element as any).__cleanup
    if (typeof cleanup === 'function') {
      try {
        cleanup()
      } catch (error) {
        console.error('Error cleaning up cell content:', error)
      }
      delete (element as any).__cleanup
    }
  }
}
